import { queryOptions } from "@tanstack/react-query";
import { api, errorOf } from "./api";

/**
 * The signed-in staff member, from GET /v1/me: user, active company, companies, roles and
 * permissions. null when nobody is signed in.
 */
async function fetchMe() {
  const { data, error, response } = await api.GET("/v1/me");
  if (response.status === 401) return null;
  if (error || !data) throw errorOf(error);
  return data;
}

export type Me = NonNullable<Awaited<ReturnType<typeof fetchMe>>>;

export const meQuery = queryOptions({ queryKey: ["me"], queryFn: fetchMe, staleTime: 60_000 });

/** An error from better-auth (`/api/auth/*`), or a network failure in the same shape. */
export interface AuthError {
  code: string;
  message: string;
  status: number;
}

export const isAuthError = (e: unknown): e is AuthError =>
  typeof e === "object" && e !== null && typeof (e as AuthError).code === "string";

/**
 * better-auth isn't in the OpenAPI document, so its few calls are plain fetches. They say which
 * product is calling themselves: a proxy in front of the API (Vercel's rewrites) may not add it.
 */
async function authPost(path: string, body: unknown, headers: Record<string, string> = {}): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(`/api/auth${path}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-client-app": "admin", ...headers },
      body: JSON.stringify(body),
    });
  } catch {
    throw { code: "NETWORK_ERROR", message: "No connection.", status: 0 } satisfies AuthError;
  }
  const data = (await response.json().catch(() => null)) as { code?: string; message?: string } | null;
  if (!response.ok) {
    throw {
      code: data?.code ?? `HTTP_${response.status}`,
      message: data?.message ?? response.statusText,
      status: response.status,
    } satisfies AuthError;
  }
  return data;
}

export const signIn = (email: string, password: string) => authPost("/sign-in/email", { email, password });

/** Sets the password from an invitation link's token (better-auth's reset-password). */
export const setPassword = (token: string, newPassword: string) => authPost("/reset-password", { token, newPassword });

/**
 * Forgot password: better-auth's request-password-reset. It answers the same whether or not the
 * email is staff's; staff get a link to /set-password?reset=1 by email (modules/staff).
 */
export const requestPasswordReset = (email: string) => authPost("/request-password-reset", { email });

export const signOut = () => authPost("/sign-out", {});

/**
 * A new phone number (P8-54): better-auth texts a code through the company's SMS integration
 * (named by X-Company), and the code confirms the number, which is then saved as verified.
 */
export const sendPhoneCode = (phoneNumber: string, companySlug: string) =>
  authPost("/phone-number/send-otp", { phoneNumber }, { "x-company": companySlug });

export const confirmPhone = (phoneNumber: string, code: string, companySlug: string) =>
  authPost("/phone-number/verify", { phoneNumber, code, updatePhoneNumber: true }, { "x-company": companySlug });

/** Changes the signed-in person's password; their other sessions end. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  authPost("/change-password", { currentPassword, newPassword, revokeOtherSessions: true });

/** Switches the session to another company; returns the new /v1/me. */
export async function chooseCompany(companyId: string): Promise<Me> {
  const { data, error } = await api.POST("/v1/me/company", { body: { company_id: companyId } });
  if (error || !data) throw errorOf(error);
  return data;
}
